// Who gets which Popular guests view, and what a host may send from it. Pure,
// no Firebase, so each rule can be tested on its own.
//
// Every rule here is a client twin of a server check (guestPayouts/hostAccess.js
// and sendGuestPayoutInvite in the functions repo). The server decides; these
// only keep the panel from offering something the server will refuse.

/**
 * Which Popular tab this signed-in person gets: "admin" (the operator tab),
 * "host" (the ranking plus Reach out, on their own events) or null (no tab).
 *
 * The host grant lives on their own vendors doc, which the panel already reads
 * fresh on every load, so a new grant shows after a reload and costs no read.
 * Only `true` counts: a revoked grant keeps the map with `enabled: false`.
 */
export function popularMode(globalState) {
  if (globalState?.isSuperAdmin) return "admin";
  if (globalState?.vendor?.popularGuests?.enabled === true) return "host";
  return null;
}

// A Firestore doc id, the way the server's isDocId reads one: the predicate
// below refuses the same ids the server's twin does.
function isDocId(value) {
  return typeof value === "string" && value.length > 0 && value.length <= 128 && !value.includes("/");
}

// Single-event coupons carry `eventID` (legacy docs only have this); newer ones
// also write `eventIDs`, and multi-event ones write only `eventIDs`. Mirrors
// couponEventIDs in the functions repo's coupons/coupons.js.
function couponEventIDs(coupon) {
  if (Array.isArray(coupon?.eventIDs) && coupon.eventIDs.length) return coupon.eventIDs;
  return coupon?.eventID ? [coupon.eventID] : [];
}

/**
 * Whether a host may put this coupon in a Popular text for this event.
 *
 * Never a global coupon: that code works at every event on Gist, and a host
 * texting it out would be handing out the platform's discount. Otherwise it has
 * to work at this event, and be either the host's own (a single-event coupon
 * carries the event's main host as vendorID, whoever made it) or one that works
 * at this event only (legacy coupons carry no vendorID at all). A multi-event
 * coupon an operator made across several hosts' events is neither, so it stays
 * with the operators.
 *
 * The second half would also pass a co-host, but the panel never gets that far
 * for one: coupons stay with the main host (see utils/eventHosts.js), and the
 * coupon list a co-host would need refuses them, so HostPopularGuests does not
 * ask for it. This only decides what the picker offers; the server decides.
 */
export function hostMayUseCoupon(coupon, { uid, eventID } = {}) {
  if (!coupon || typeof coupon !== "object" || !isDocId(uid) || !isDocId(eventID)) return false;
  if (coupon.allEvents === true) return false;
  const ids = couponEventIDs(coupon);
  if (!ids.includes(eventID)) return false;
  if (coupon.vendorID === uid) return true;
  return ids.every((id) => id === eventID);
}

// Mirrors DEFAULT_DURATION_HOURS in the functions repo's autoCompleteEvents.js.
const DEFAULT_DURATION_HOURS = 2;
const HOUR_MS = 60 * 60 * 1000;
const ZONED_ISO = /(?:[zZ]|[+-]\d{2}:?\d{2})$/;

// The event's start in ms, from the shapes the panel actually holds: a
// Firestore Timestamp off the event stream, or a Date after a form edit. A
// zoneless string is Pacific wall-clock on the server, which the panel does
// not convert, so it reads as unknown here and the server decides.
function startMillis(date) {
  if (date == null) return null;
  let ms = null;
  if (typeof date.toMillis === "function") ms = date.toMillis();
  else if (typeof date.toDate === "function") ms = date.toDate().getTime();
  else if (date instanceof Date) ms = date.getTime();
  else if (typeof date === "number") ms = date;
  else if (typeof date === "string" && ZONED_ISO.test(date.trim())) ms = new Date(date).getTime();
  else if (typeof date === "object" && typeof (date.seconds ?? date._seconds) === "number") {
    ms = (date.seconds ?? date._seconds) * 1000;
  }
  return Number.isFinite(ms) ? ms : null;
}

/**
 * "cancelled", "over" or null: why a host cannot reach out from this event.
 *
 * Client twin of eventClosedReason in guestPayouts/hostAccess.js, which refuses
 * every host send on such an event. Over is `isCompleted`, or the start plus
 * the event's `duration` (hours, else 2) already past. A date the panel cannot
 * read is not proof the night is over, so it passes and the server decides.
 */
export function eventClosedReason(event, nowMs = Date.now()) {
  if (!event) return null;
  if (event.status === "cancelled") return "cancelled";
  if (event.isCompleted === true) return "over";
  const start = startMillis(event.date);
  if (start == null) return null;
  const hours = Number(event.duration);
  const length = (Number.isFinite(hours) && hours > 0 ? hours : DEFAULT_DURATION_HOURS) * HOUR_MS;
  return nowMs >= start + length ? "over" : null;
}

// The server's own words for the two earn refusals (HOST_EARN_SELF_MESSAGE and
// HOST_EARN_OFF_MESSAGE in guestPayouts/hostAccess.js).
export const HOST_EARN_SELF_LINE = "You can't send the earn text to yourself.";
export const HOST_EARN_OFF_LINE = "Gist has turned earning off for this member.";

/**
 * Why a host may not tick the earn nudge for this member, or null.
 *
 * Client twin of the two host-only earn checks in sendGuestPayoutInvite, in the
 * server's order: the nudge makes its target eligible to turn payouts on, so a
 * host may not send it to themselves, nor to a member Gist took out of earning
 * (the row's `payouts.earnOff`, which only a host's rows carry).
 */
export function hostEarnBlockedReason(guest, uid) {
  if (isDocId(uid) && guest?.userID === uid) return HOST_EARN_SELF_LINE;
  if (guest?.payouts?.earnOff === true) return HOST_EARN_OFF_LINE;
  return null;
}

/**
 * Whether a refusal means the host's access was taken away mid-session, as
 * opposed to something about this one request (someone else's event, a coupon
 * that is not theirs). Only the first one empties the tab.
 */
export function isAccessRevoked(err) {
  return err?.code === "functions/permission-denied" && /not turned on/i.test(err?.message || "");
}
